import { boolean, string } from 'fp-ts';
import normalizeException from 'normalize-exception';
/**
 * Parses an unknown object into an Error instance.
 *
 * This takes care of edge cases like `throw null` that are legal but error-prone in JS.
 */
export const fromThrown = normalizeException;
/**
 * Immediately halts the program with an error message and stack trace.
 */
export function panic(message, cause) {
    const error = new Error(message, { cause });
    error.name = 'FatalError';
    throw error;
}
/**
 * Assert that the given condition will always hold, throw an invariant violation otherwise.
 */
export const invariant = (arg1, arg2, arg3) => {
    if (boolean.isBoolean(arg1)) {
        if (!arg1)
            panic(arg2, arg3);
    }
    else {
        return (condition) => {
            if (!condition)
                panic(arg1, arg2);
        };
    }
};
/**
 * Assert that the given value is never null, throw an invariant violation otherwise.
 */
export const assertNonNull = (value, arg1, arg2) => {
    if (string.isString(arg1))
        invariant(value != null, arg1, arg2);
    else
        invariant(value != null, 'Non-null assertion failed', arg1);
};
/**
 * Assert that the given value is not null and return it, throw an invariant violation otherwise.
 */
export const requireNonNull = (value, ...args) => {
    assertNonNull(value, ...args);
    return value;
};
/**
 * This function should never be called. It can be used to ensure switch/case statements are
 * exhaustive, i.e. cover all cases.
 *
 * @example
 * declare const val: 'a' | 'b'
 * switch (val) {
 *   case 'a': return 'A'
 *   case 'b': return 'B'
 *   default: never(val)
 * }
 */
export function never(value) {
    throw new Error('This function should never be called', { cause: { calledWith: value } });
}
//# sourceMappingURL=invariant.js.map